import React from 'react';

import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Below lg, the same breakpoint AdminLayout switches at. For the few choices CSS cannot make, such
// as where a link should land on a phone.
export const isPhoneLayout = (): boolean =>
  window.matchMedia('(max-width: 1023.98px)').matches;

interface MobilePageBarProps {
  title: string;
  // Where the back arrow goes. A fixed route, not history.back(): a member who opened the page
  // from a link or a fresh tab has no history to go back to.
  backTo: string;
  backLabel: string;
  // Replaces the plain jump to backTo, for a page that has its own step to undo first (the golf
  // lists close an open country before leaving).
  onBack?: () => void;
  // One control on the right, such as the profile's Settings gear.
  action?: React.ReactNode;
}

// The phone top bar for the member's own pages (profile, settings). On a phone those pages work
// like an app, not like the admin: one back arrow, a title and at most one action, no logo, no
// theme switch, no account menu and no breadcrumbs. AdminLayout shows it in place of Topbar and the
// breadcrumb bar below lg; from lg up the desktop layout is unchanged.
const MobilePageBar: React.FC<MobilePageBarProps> = ({
  title,
  backTo,
  backLabel,
  onBack,
  action,
}) => {
  const navigate = useNavigate();

  return (
    <header className="grid h-14 grid-cols-[3rem_1fr_3rem] items-center border-b border-primary/40 bg-secondary px-1">
      <button
        type="button"
        onClick={onBack ?? ((): void => void navigate(backTo))}
        aria-label={backLabel}
        className="flex h-12 w-12 items-center justify-center rounded-full text-primary transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>
      <p className="truncate text-center font-display text-base font-semibold text-white">
        {title}
      </p>
      <div className="flex justify-end">{action}</div>
    </header>
  );
};

// The round icon button the bar's action slot expects, so every page's action looks the same.
export const MobilePageBarButton: React.FC<{
  label: string;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
}> = ({ label, onClick, icon: Icon }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="flex h-12 w-12 items-center justify-center rounded-full text-primary transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
  >
    <Icon className="h-6 w-6" />
  </button>
);

export default MobilePageBar;
